import React, { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState(token ? "checking" : "error");
  const [email, setEmail] = useState("");
  const [resending, setResending] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true; // guard against StrictMode double-invoke consuming the single-use token twice
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setStatus("success");
      } catch {
        setStatus("error");
      }
    })();
  }, [token]);

  const resend = async (e) => {
    e.preventDefault();
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email });
      toast.success("If this email needs verification, we've sent a new link.");
    } catch {
      toast.success("If this email needs verification, we've sent a new link.");
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthShell
      title="Email verification"
      footer={<Link to="/login" className="font-medium text-[#0D9488] hover:underline">Back to log in</Link>}
    >
      {status === "checking" ? (
        <div className="flex items-center justify-center py-6 text-slate-500" data-testid="verify-checking">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Verifying your email…
        </div>
      ) : status === "success" ? (
        <div className="space-y-5" data-testid="verify-success">
          <div className="rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            <p className="font-semibold">Email verified — please log in.</p>
            <p className="mt-1">Your account is now active. You can log in to start creating reference pages.</p>
          </div>
          <Link to="/login" data-testid="verify-login-link">
            <Button className="w-full bg-[#0A2540] text-white hover:bg-[#0c2e4e]">Go to log in</Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-5" data-testid="verify-error">
          <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
            <p className="font-semibold">This verification link is invalid or has expired.</p>
            <p className="mt-1">Request a new verification email below.</p>
          </div>
          <form onSubmit={resend} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="verify-email">Email</Label>
              <Input id="verify-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="verify-email-input" />
            </div>
            <Button type="submit" disabled={resending} className="w-full bg-[#0A2540] text-white hover:bg-[#0c2e4e]" data-testid="verify-resend-btn">
              {resending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Resend verification email
            </Button>
          </form>
        </div>
      )}
    </AuthShell>
  );
}
